import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Progress, 
  Radio, 
  Modal,
  message,
  Spin
} from 'antd';
import { 
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useExamQuestions, submitExamResult } from '../hooks/exam';
import { useQuestionAnswers, useSaveAnswerResult } from '../hooks/lesson';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import authService from '../services/authService';

const { Title, Text } = Typography;

interface UserAnswer {
  questionId: string;
  answerId: string;
  isCorrect: boolean;
}

const ExamTaking: React.FC = () => {
  const { isMobile } = useResponsive();  
  const navigate = useNavigate();
  const location = useLocation();
  const { examId } = useParams<{ examId: string }>();
  
  // Get exam data from navigation state
  const examData = location.state || {};
  const {
    examTitle = 'Mock Exam 1',
    totalMarks = 100,
    passMarks = 70,
    duration = 60 // minutes
  } = examData;

  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, UserAnswer>>({});
  const [timeRemaining, setTimeRemaining] = useState(duration * 60); // Convert to seconds
  const [isExamFinished, setIsExamFinished] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Fetch exam questions
  const { data: examQuestions, isLoading: questionsLoading } = useExamQuestions(examId);
  
  // Get current question
  const currentQuestion = examQuestions?.[currentQuestionIndex];
  
  // Fetch answers for current question
  const { data: questionAnswers, isLoading: answersLoading } = useQuestionAnswers(
    currentQuestion?.id?.toString()
  );

  // Save answer result mutation
  const saveAnswerResult = useSaveAnswerResult();

  // Apply security protection
  useSecurityProtection();

  // Timer effect
  useEffect(() => {
    if (timeRemaining <= 0) {
      handleAutoSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Format time remaining
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle answer selection
  const handleAnswerSelect = async (answerId: string) => {
    if (!currentQuestion || !examId) return;
    
    const selectedAnswer = questionAnswers?.find((answer: any) => answer.id.toString() === answerId);
    const isCorrect = selectedAnswer?.is_correct === 1;
    
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: {
        questionId: currentQuestion.id.toString(),
        answerId,
        isCorrect
      }
    }));

    // Save answer result to backend
    try {
      const user = authService.getCurrentUser();
      if (user?.id) {
        await saveAnswerResult.mutateAsync({
          user_id: Number(user.id),
          question_id: Number(currentQuestion.id),
          point: isCorrect ? 1 : 0,
          exam_id: Number(examId)
        });
        console.log('Answer result saved successfully');
      }
    } catch (error) {
      console.error('Failed to save answer result:', error);
      // Continue with the exam even if save fails
    }
  };

  // Navigation functions
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < (examQuestions?.length || 0)) {
      setCurrentQuestionIndex(index);
    }
  };

  const goToPreviousQuestion = () => {
    goToQuestion(currentQuestionIndex - 1);
  };

  const goToNextQuestion = () => {
    goToQuestion(currentQuestionIndex + 1);
  };

  // Submit exam
  const handleSubmitExam = useCallback(() => {
    setShowSubmitModal(true);
  }, []);

  const handleAutoSubmit = useCallback(() => {
    message.warning('Time is up! Exam will be submitted automatically.');
    confirmSubmit();
  }, []);

  const confirmSubmit = async () => {
    // Calculate results
    const totalQuestions = examQuestions?.length || 0;
    const correctAnswers = Object.values(userAnswers).filter(answer => answer.isCorrect).length;
    const score = (correctAnswers / totalQuestions) * totalMarks;
    const passed = score >= passMarks;

    setIsExamFinished(true);
    setShowSubmitModal(false);

    // Save exam result to backend
    try {
      const user = authService.getCurrentUser();
      const userId = user?.id || 1;

      await submitExamResult({
        user_id: parseInt(userId.toString()),
        exam_id: parseInt(examId || '0'),
        score: Math.round(score),
        total_score: totalQuestions,
        passed: passed ? 1 : 0
      });

      console.log('Exam result saved successfully');
    } catch (error) {
      console.error('Failed to save exam result:', error);
      // Continue to results page even if save fails
    }

    // Navigate to results page
    navigate(`/exam/${examId}/results`, {
      state: {
        examTitle,
        totalQuestions,
        correctAnswers,
        score,
        passed,
        userAnswers,
        examData
      }
    });
  };

  // Loading state
  if (questionsLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!examQuestions || examQuestions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text>No questions found for this exam.</Text>
      </div>
    );
  }

  const progressPercentage = ((currentQuestionIndex + 1) / examQuestions.length) * 100;

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: '#52c41a',
        borderRadius: '12px',
        padding: isMobile ? '16px' : '20px',
        marginBottom: '20px',
        color: 'white'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              {examTitle}
            </Title>
          </Col>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ fontSize: '20px' }} />
              <Text strong style={{ color: 'white', fontSize: '18px' }}>
                {formatTime(timeRemaining)}
              </Text>
            </Space>
          </Col>
        </Row>
        
        {/* Progress */}
        <div style={{ marginTop: '12px' }}>
          <Progress 
            percent={progressPercentage} 
            showInfo={false}
            strokeColor="#fff"
            trailColor="rgba(255,255,255,0.3)"
          />
          <Text style={{ color: 'white', fontSize: '14px' }}>
            Question {currentQuestionIndex + 1} of {examQuestions.length}
          </Text>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Question Navigation Sidebar */}
        <Col xs={24} lg={6}>
          <Card 
            title="Questions"
            style={{ 
              borderRadius: '12px',
              height: 'fit-content'
            }}
          >
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '8px'
            }}>
              {examQuestions.map((_, index) => {
                const isAnswered = examQuestions[index] && userAnswers[examQuestions[index].id];
                const isCurrent = index === currentQuestionIndex;
                
                return (
                  <Button
                    key={index}
                    size="small"
                    type={isCurrent ? 'primary' : isAnswered ? 'default' : 'text'}
                    onClick={() => goToQuestion(index)}
                    style={{
                      backgroundColor: isCurrent 
                        ? '#52c41a' 
                        : isAnswered 
                        ? '#e6f7ff' 
                        : '#f5f5f5',
                      borderColor: isCurrent 
                        ? '#52c41a' 
                        : isAnswered 
                        ? '#1890ff' 
                        : '#d9d9d9',
                      color: isCurrent 
                        ? 'white' 
                        : isAnswered 
                        ? '#1890ff' 
                        : '#595959'
                    }}
                  >
                    {index + 1}
                  </Button>
                );
              })}
            </div>
          </Card>
        </Col>

        {/* Main Question Area */}
        <Col xs={24} lg={18}>
          <Card 
            style={{ 
              borderRadius: '12px',
              minHeight: '500px'
            }}
          >
            {/* Question Header */}
            <div style={{ marginBottom: '24px' }}>
              <Text strong style={{ color: '#8c8c8c', fontSize: '14px' }}>
                Question: {currentQuestionIndex + 1}
              </Text>
              <br />
              <Text strong style={{ color: '#8c8c8c', fontSize: '14px' }}>
                Section: 1
              </Text>
            </div>

            {/* Question Text */}
            <div 
              dangerouslySetInnerHTML={{ 
                __html: currentQuestion?.text || 'Question text not available' 
              }}
              style={{ 
                marginBottom: '32px',
                fontSize: isMobile ? '16px' : '18px',
                lineHeight: '1.6',
                color: '#1f1f1f'
              }}
            />

            {/* Answer Options */}
            <div style={{ marginBottom: '40px' }}>
              {answersLoading ? (
                <Spin size="large" style={{ display: 'block', textAlign: 'center' }} />
              ) : (
                <Radio.Group
                  value={userAnswers[currentQuestion?.id]?.answerId}
                  onChange={(e) => handleAnswerSelect(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {questionAnswers?.map((answer: any) => (
                      <Radio 
                        key={answer.id}
                        value={answer.id.toString()}
                        style={{ 
                          padding: '12px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '8px',
                          width: '100%',
                          margin: 0
                        }}
                      >
                        <Text style={{ fontSize: '16px', marginLeft: '8px' }}>
                          {answer.option_text}
                        </Text>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              )}
            </div>

            {/* Navigation Buttons */}
            <Row justify="space-between" align="middle">
              <Col>
                <Button
                  icon={<LeftOutlined />}
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  size="large"
                >
                  Back
                </Button>
              </Col>
              
              <Col>
                {currentQuestionIndex === examQuestions.length - 1 ? (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    onClick={handleSubmitExam}
                    style={{
                      backgroundColor: '#52c41a',
                      borderColor: '#52c41a'
                    }}
                  >
                    Finish Test
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<RightOutlined />}
                    onClick={goToNextQuestion}
                    size="large"
                  >
                    Next
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Submit Confirmation Modal */}
      <Modal
        title="Submit Exam"
        open={showSubmitModal}
        onOk={confirmSubmit}
        onCancel={() => setShowSubmitModal(false)}
        okText="Submit"
        cancelText="Continue Exam"
      >
        <p>Are you sure you want to submit your exam?</p>
        <p>
          You have answered {Object.keys(userAnswers).length} out of {examQuestions.length} questions.
        </p>
        <p>Once submitted, you cannot make any changes.</p>
      </Modal>

      {/* Exam Question HTML Content Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* Table Styles */
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: ${isMobile ? '16px' : '20px'} 0 !important;
          background: white !important;
          border: 1px solid #e0e0e0 !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
        }
        
        th, td {
          padding: ${isMobile ? '8px 12px' : '10px 14px'} !important;
          text-align: left !important;
          border: 1px solid #e0e0e0 !important;
          font-size: ${isMobile ? '13px' : '14px'} !important;
          color: #333 !important;
        }
        
        th {
          background: #fafafa !important;
          font-weight: 600 !important;
          color: #1f1f1f !important;
        }
        
        tbody tr:hover {
          background: #f5f5f5 !important;
        }
        
        colgroup col {
          border: none !important;
        }
        
        /* Text Formatting */
        strong, b {
          font-weight: 600 !important;
          color: #1f1f1f !important;
        }
        
        em, i {
          font-style: italic !important;
          color: #34495e !important;
        }
        
        p {
          margin: ${isMobile ? '8px' : '12px'} 0 !important;
          line-height: 1.6 !important;
          color: #333 !important;
        }
        
        /* Lists */
        ul, ol {
          margin: ${isMobile ? '12px' : '16px'} 0 !important;
          padding-left: ${isMobile ? '20px' : '24px'} !important;
        }
        
        li {
          margin: ${isMobile ? '6px' : '8px'} 0 !important;
          line-height: 1.6 !important;
          color: #333 !important;
        }
        
        /* Code */
        code {
          background: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 4px !important;
          padding: 2px 6px !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: ${isMobile ? '13px' : '14px'} !important;
          color: #e83e8c !important;
        }
        
        pre {
          background: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 8px !important;
          padding: ${isMobile ? '12px' : '16px'} !important;
          margin: ${isMobile ? '16px' : '20px'} 0 !important;
          overflow-x: auto !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: ${isMobile ? '13px' : '14px'} !important;
          line-height: 1.5 !important;
        }
        `
      }} />
    </div>
  );
};

export default ExamTaking;
